import React, { useId } from 'react';
import { createPortal } from 'react-dom';

import IconButton from './IconButton';
import { CloseIcon } from './icons';
import { useOverlay } from './useOverlay';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  /** Short line under the title. */
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  /** Usually a ghost Cancel and one primary. Pinned below a scrolling body. */
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Turn off for forms a stray click would lose. Esc still closes. */
  closeOnBackdrop?: boolean;
  /** A short message: stays a small card on a phone rather than a full-screen sheet. */
  compact?: boolean;
  className?: string;
}

/** Portalled, with Esc, a focus trap and scroll lock. A full-screen sheet on a phone. */
const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = 'md',
  closeOnBackdrop = true,
  compact = false,
  className,
}) => {
  const ref = useOverlay<HTMLDivElement>(open, onClose);
  const titleId = useId();
  if (!open) return null;

  return createPortal(
    <div
      className={compact ? 'ui-scrim ui-scrim--alert' : 'ui-scrim'}
      onMouseDown={(event) => {
        // mousedown, so a drag that starts inside and ends out does not close it.
        if (closeOnBackdrop && event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={['ui-modal', `ui-modal--${size}`, className || ''].filter(Boolean).join(' ')}
      >
        <div className="ui-modal-head">
          <div>
            <h2 className="ui-modal-title" id={titleId}>
              {title}
            </h2>
            {subtitle && <p className="ui-modal-sub">{subtitle}</p>}
          </div>
          <IconButton label="Close" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>
        <div className="ui-modal-body">{children}</div>
        {footer && <div className="ui-modal-foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
